import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import { forwardRef, useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import { useUncontrolled } from '../../hooks/useUncontrolled.js';
import { ClearButton } from './ClearButton.js';
import { InputBase } from './InputBase.js';
import defaultStyles from './styles/Input.module.scss.js';
import { getInputClasses, inputAddon } from './utils/input-helper.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { contains } from '../../utils/dom.js';
import { isUndefined } from '../../utils/is.js';

const ID_SUFFIX = '_input';
const Input = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    theme,
    addBefore,
    addAfter,
    suffix,
    prefix,
    beforeStyle,
    afterStyle,
    disabled,
    allowClear = false,
    onChange,
    variant = 'default',
    size = 'md',
    validationStatus,
    label,
    name,
    value,
    defaultValue,
    readOnly,
    onClear,
    placeholder,
    idSuffix = ID_SUFFIX,
    autoFocus = false
  } = props;
  const inputRef = useRef();
  const inputWrapperRef = useRef();
  const [focus, setFocus] = useState(autoFocus);
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  // Check if input has label
  const hasNoLabel = isUndefined(label) || label?.length <= 0;
  // Should show label
  const [_value, handleChange] = useUncontrolled({
    value,
    defaultValue,
    fallbackValue: '',
    onChange
  });
  const notEmpty = allowClear && !disabled && !!_value.length;
  const withAddons = addBefore || addAfter;
  const withPrefixes = suffix || prefix;
  const hasSuffix = suffix || notEmpty;
  const isComplexInput = withPrefixes || allowClear;
  const {
    inputClassNames,
    labelClassNames,
    innerClassNames
  } = getInputClasses({
    clsx,
    styles,
    size,
    variant,
    suffix: hasSuffix,
    disabled,
    validationStatus,
    prefix,
    allowClear,
    value: _value,
    focus,
    withPrefixes,
    hasClear: notEmpty,
    withLabel: !hasNoLabel
  });
  const elementHandles = {
    onMouseDown: e => {
      const node = e.target;
      if (e.target.tagName !== 'INPUT' && inputWrapperRef.current && contains(inputWrapperRef.current, node)) {
        e.preventDefault();
      }
    },
    onClick: e => {
      const node = e.target;
      if (inputWrapperRef.current && contains(inputWrapperRef.current, node)) {
        inputRef.current && inputRef.current.focus();
      }
    }
  };
  // Input field can only show an inset label
  const labelElement = /*#__PURE__*/jsx("label", {
    className: labelClassNames,
    htmlFor: name + idSuffix,
    "aria-labelledby": name + idSuffix,
    children: label
  });
  const inputElement = /*#__PURE__*/jsx(InputBase, {
    ref: mergeRefs([inputRef, ref]),
    ...props,
    autoFocus: autoFocus,
    id: name + idSuffix,
    className: inputClassNames,
    placeholder: hasNoLabel ? placeholder : '',
    onFocus: e => {
      if (!disabled && !readOnly) {
        setFocus(true);
        props.onFocus && props.onFocus(e);
      }
    },
    onBlur: e => {
      setFocus(false);
      props.onBlur && props.onBlur(e);
    },
    value: _value,
    onValueChange: handleChange
  });
  const clearButton = notEmpty ? /*#__PURE__*/jsx(ClearButton, {
    theme: {
      root: styles.clear
    },
    onClick: e => {
      e.stopPropagation();
      handleChange('', e);
      if (!focus) {
        inputRef.current && inputRef.current.focus();
      }
      onClear && onClear(e);
    }
  }) : null;
  const insetInput = /*#__PURE__*/jsxs("div", {
    className: styles.box,
    children: [labelElement, inputElement]
  });

  // If input does not have a label then display simple input block
  const input = !hasNoLabel ? insetInput : inputElement;
  const inputWithPrefix = isComplexInput && /*#__PURE__*/jsxs("div", {
    ref: !withAddons ? inputWrapperRef : null,
    ...(!withAddons && elementHandles),
    className: clsx(!withAddons && styles.root, isComplexInput && innerClassNames),
    children: [prefix && inputAddon(styles.prefix, prefix), input, (suffix || clearButton) && inputAddon(styles.suffix, suffix, null, clearButton)]
  });
  const inputWithAddons = withAddons && /*#__PURE__*/jsxs("div", {
    ...elementHandles,
    ref: inputWrapperRef,
    className: clsx(styles.root, styles.has_addons, {
      [styles.has_before]: addBefore && !addAfter,
      [styles.has_after]: !addBefore && addAfter,
      [styles.has_both]: addBefore && addAfter
    }),
    children: [inputAddon(styles.before, addBefore, beforeStyle), inputWithPrefix || input, inputAddon(styles.after, addAfter, afterStyle)]
  });
  const complexInput = inputWithAddons || inputWithPrefix;
  const simpleInput = insetInput || inputElement;
  return complexInput || simpleInput;
});
Input.displayName = 'Input';

export { Input };
